import { jwtDecode } from "jwt-decode";

/**
 * Claim type nopCommerce uses for roles. It matches the long-form
 * `ClaimTypes.Role` URI emitted by the .NET `JwtSecurityTokenHandler`.
 */
export const ROLE_CLAIM_TYPE = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

export interface JwtPayload {
  nbf: string;
  exp: string;
  CustomerId: string;
  /**
   * The .NET token handler collapses a lone role claim into a bare JSON
   * string and only emits an array when the customer carries two or more
   * roles, so this claim has to be typed as a union.
   */
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": string | string[];
  [key: string]: unknown;
}

/**
 * Reads the role claims of a token, always returning an array.
 *
 * The .NET token handler writes a single role as a JSON string, so the raw
 * claim has to be normalized here. Without this, every caller that assumes an
 * array (`.some`, `.includes`, `.map`) breaks for the majority of customers,
 * who only carry the "Registered" role.
 */
export const decodeRoles = (token: string): string[] => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const claim = decoded[ROLE_CLAIM_TYPE];
    if (Array.isArray(claim)) return claim;
    if (typeof claim === "string") return [claim];
    return [];
  } catch (error) {
    console.error("Invalid token:", error);
    return [];
  }
};

/**
 * Clock-skew buffer in seconds. A token is considered expired slightly before
 * its actual `exp` claim so that a request dispatched right at the boundary
 * still arrives with a valid token.
 *
 * This is the **single source of truth** for the expiry buffer. All token
 * expiry checks (in token-manager, ApiClient 401 interceptor, etc.) should
 * use {@link isTokenExpired} rather than reimplementing the logic.
 */
export const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    // exp is seconds since epoch, convert to milliseconds for Date.now()
    const exp = typeof decoded.exp === "string" ? parseInt(decoded.exp, 10) : decoded.exp;
    if (typeof exp !== "number" || isNaN(exp)) return true;
    const now = Math.floor(Date.now() / 1000);
    return exp < now + TOKEN_EXPIRY_BUFFER_SECONDS;
  } catch (error) {
    console.error("Invalid token:", error);
    return true;
  }
};

export const isUser = (token: string): boolean => {
  return !isGuest(token);
};

export const isGuest = (token: string): boolean => {
  return token !== null && token !== undefined && !isTokenExpired(token) && decodeRoles(token).includes("Guest");
};

/**
 * Returns true if the token belongs to a vendor account.
 * NopCommerce assigns the "Vendors" system role to vendor customers.
 * Returns false on any invalid, malformed, or expired input - never throws.
 */
export const isVendor = (token: string): boolean => {
  try {
    return token !== null && token !== undefined && !isTokenExpired(token) && decodeRoles(token).includes("Vendors");
  } catch {
    return false;
  }
};